//dependecies
import { animated, useTransition } from "@react-spring/web";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

//hooks
import useDisableMainScroll from "../../hooks/useDisableMainScroll";

//utils
import getSizeClass from "./utils";

//components
import CloseButton from "../Button/templates/CloseButton";
// css
import css from "./Modal.module.scss";

// These props should be consistent across most modal templates
export type BaseModalProps = {
	/**
	 * The content of the modal. Rendered in the body of the modal.
	 */
	children?: React.ReactNode;
	/**
	 * The open state of the modal
	 */
	isOpen?: boolean;
	/**
	 * When the user clicks the close button, this function is called. The
	 * parent component should unmount the modal when this function is invoked.
	 * @returns void
	 */
	handleClose: () => void;
	/**
	 * The size of the modal. Can be "small", "large", "extralarge", "full" or "auto".
	 * @default small
	 * @type "small" | "large" | "extralarge" | "full" | "auto"
	 */
	size?: "small" | "large" | "extralarge" | "full" | "auto";
	/**
	 * The style of modal wrapper.
	 * @default null
	 * @type scss class
	 */
	wrapperClassName?: string;
	/**
	 * should modal run handleClose on click outside
	 */
	closeOnClickOutside?: boolean;
	/**
	 * remove the close button
	 */
	hideCloseButton?: boolean;
};

/** This is a base, unstyle modal to build templates from */
const ModalContent = ({ children, handleClose, wrapperClassName, hideCloseButton }: BaseModalProps) => {
	return (
		<div data-testid="reactModal" className={`${css.modal} ${wrapperClassName}`}>
			{!hideCloseButton && (
				<div data-testid="closeButtonContainer" className={css.closeButtonContainer}>
					<CloseButton onClick={handleClose} />
				</div>
			)}

			<div data-testid="modalContent" className={css.modalContent}>
				{children}
			</div>
		</div>
	);
};
// This is the base modal that is used to render all modals
// using the portal takes it outside of the dom hierarchy and attaches it to the body
const BaseModal = ({
	isOpen = true,
	handleClose,
	size = "auto",
	wrapperClassName,
	closeOnClickOutside = false,
	children,
	hideCloseButton = false,
}: BaseModalProps) => {
	const modalRoot = useRef(document.createElement("div"));

	const transitions = useTransition(isOpen, {
		from: { opacity: 0.3, y: -10 },
		enter: { opacity: 1, y: 0 },
		leave: { opacity: 0, y: 20 },
	});

	// On mount we want to add a div to attach the panel to
	// On dismount we want to remove the div
	useEffect(() => {
		document.body.appendChild(modalRoot.current);

		return () => {
			modalRoot.current.remove();
		};
	}, []);

	const [disableScroll, setDisableScroll] = useState(false);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDisableScroll(isOpen);
		}, 300);

		return () => {
			clearTimeout(timeout);
		};
	}, [isOpen]);

	useDisableMainScroll({ enabled: disableScroll });

	const sizeClass = getSizeClass(size);

	return transitions((style, isOpen) => {
		return isOpen
			? createPortal(
					<animated.div
						id="py-base-modal"
						style={{ opacity: style.opacity }}
						className={css.modalOuterContainer}
					>
						<animated.div style={{ ...style }} className={`${css.modalContainer} ${css[sizeClass]}`}>
							<ModalContent
								isOpen={isOpen}
								handleClose={handleClose}
								size={size}
								wrapperClassName={wrapperClassName}
								hideCloseButton={hideCloseButton}
							>
								{children}
							</ModalContent>
						</animated.div>
						<div className={css.modalBackdrop} onClick={closeOnClickOutside ? handleClose : undefined} />
					</animated.div>,
					modalRoot.current
				)
			: null;
	});
};

export default BaseModal;
